import { createContext, useCallback, useContext, useEffect, useState } from "react";

import { useApi } from "./ApiContext";
import { useAuth } from "./AuthContext";

const DataContext = createContext(null);

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error("useData must be used within a DataProvider");
  }
  return context;
};

export const DataProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const api = useApi();

  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);

  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);

  // Initialize globalProjectId from localStorage or default to "ALL"
  const [globalProjectId, setGlobalProjectId] = useState(() => {
    return localStorage.getItem("activeProjectId") || "ALL";
  });

  // Persist globalProjectId to localStorage whenever it changes
  useEffect(() => {
    if (globalProjectId) {
      localStorage.setItem("activeProjectId", globalProjectId);
    } else {
      localStorage.removeItem("activeProjectId");
    }
  }, [globalProjectId]);

  const fetchProjects = useCallback(async () => {
    if (!isAuthenticated) return;
    setProjectsLoading(true);
    try {
      const response = await api.get(`/projects`);
      setProjects(response.data.items || []);
    } catch (err) {
      console.error("Failed to fetch projects:", err);
    } finally {
      setProjectsLoading(false);
    }
  }, [isAuthenticated, api]);

  const fetchUsers = useCallback(async () => {
    if (!isAuthenticated) return;
    setUsersLoading(true);
    try {
      const response = await api.get(`/users`);
      setUsers(response.data.items || []);
    } catch (err) {
      console.error("Failed to fetch users:", err);
    } finally {
      setUsersLoading(false);
    }
  }, [isAuthenticated, api]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchProjects();
      fetchUsers();
    }
  }, [isAuthenticated, fetchProjects, fetchUsers]);

  const value = {
    projects,
    setProjects,
    refreshProjects: fetchProjects,

    users,
    setUsers,
    refreshUsers: fetchUsers,

    globalProjectId,
    setGlobalProjectId,

    loading: usersLoading || projectsLoading,
    reloadData: () => {
      fetchUsers();
      fetchProjects();
    }
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};
